import {
    Autocomplete,
    Box,
    createFilterOptions,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
} from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { ColorInput } from '@/components/Layout/ColorInput';
import { WidgetParameter } from '@/models/widget';
import { useFontLinks } from '@/pages/widgetEditor/hooks/useFontLinks';
import {
    buildGoogleFontHref,
    FONT_OPTIONS,
    humanizeLabel,
} from '@/pages/widgetEditor/utils';

interface WidgetParameterControlProps {
    name: string;
    param: WidgetParameter;
    value: string;
    /** Shown read-only when the instance follows the shared design. */
    disabled?: boolean;
    onChange: (value: string) => void;
}

const filterFonts = createFilterOptions<string>();

// Each option renders in its own font, so every suggestion must be loaded,
// not just the selected one the preview loads. Same hrefs as the preview, so
// `useFontLinks` dedupes them. ponytail: system fonts (Arial, ...) get a
// failing Google request too, as they already do in the preview.
const FONT_OPTION_HREFS = FONT_OPTIONS.map(buildGoogleFontHref);

interface FontControlProps {
    label: string;
    description?: string;
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
}

/** Font suggestions that narrow while the user types. */
const FontControl: FunctionComponent<FontControlProps> = ({
    label,
    description,
    value,
    disabled,
    onChange,
}) => {
    // Only typing filters. Opening the list on a selected font shows every
    // option instead of just the selected one, so it stays a suggestion list.
    const [isTyping, setIsTyping] = useState(false);
    useFontLinks(FONT_OPTION_HREFS);

    return (
        <Autocomplete
            freeSolo
            fullWidth
            disableClearable
            disabled={disabled}
            options={FONT_OPTIONS}
            filterOptions={(options, state) =>
                isTyping ? filterFonts(options, state) : options
            }
            // Typed text stays local until a font is picked, Enter is pressed
            // or the field loses focus, so half-typed names never reach the
            // value (and render in a browser fallback font).
            autoSelect
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            onInputChange={(_, __, reason) => setIsTyping(reason === 'input')}
            onClose={() => setIsTyping(false)}
            renderOption={({ key, ...props }, option) => (
                <Box
                    component='li'
                    key={key}
                    {...props}
                    sx={{ fontFamily: option }}
                >
                    {option}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    helperText={description}
                    inputProps={{
                        ...params.inputProps,
                        // Rethink Sans is the widgets' own default font.
                        style: { fontFamily: value || 'Rethink Sans' },
                    }}
                />
            )}
        />
    );
};

/** The editor control matching a manifest parameter's declared type. */
export const WidgetParameterControl: FunctionComponent<
    WidgetParameterControlProps
> = ({ name, param, value, disabled = false, onChange }) => {
    const label = humanizeLabel(name);

    if (param.type === 'font') {
        return (
            <FontControl
                label={label}
                description={param.description}
                value={value}
                disabled={disabled}
                onChange={onChange}
            />
        );
    }

    if (param.type === 'color') {
        return (
            <ColorInput
                fullWidth
                disabled={disabled}
                label={label}
                helperText={param.description}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }

    if (param.type === 'boolean') {
        return (
            <FormControlLabel
                control={
                    <Switch
                        disabled={disabled}
                        checked={value === 'true'}
                        onChange={(e) => onChange(String(e.target.checked))}
                    />
                }
                label={label}
            />
        );
    }

    if (param.type === 'enum' || param.options?.length) {
        return (
            <TextField
                select
                fullWidth
                disabled={disabled}
                label={label}
                helperText={param.description}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                {param.options?.map((option) => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    return (
        <TextField
            fullWidth
            disabled={disabled}
            type={param.type === 'number' ? 'number' : 'text'}
            label={label}
            helperText={param.description}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            inputProps={param.type === 'number' ? { min: 0 } : undefined}
        />
    );
};
